import type { JsonLd } from 'json-ld-types';

import { SmartCardLocalCacheClient } from '@atlaskit/editor-card-provider/smart-card-local-cache-client';
import CardClient from '@atlaskit/link-provider/client';
import { getStatus } from '@atlaskit/linking-common/utils/get-status';
import type { CardAppearance, EnvironmentsKeys } from '@atlaskit/linking-common/types';
import type { CardStore } from '@atlaskit/linking-common/store';

import { fg } from '@confluence/feature-gating';
import { ConfluenceEditorCardProvider } from '@confluence/smartlinks';
import { manuallyLogExpExposure } from '@confluence/feature-experiments';
import { hasUrlFragment } from '@confluence/content-smartlinks/entry-points/buildResolvedSmartLinkState.ts';

import { getUrlAndMetadataStatus } from './cacheKey.ts';

type onResolveFn = (data: unknown | undefined) => void;

const deferIdle = (cb: (data: unknown) => void) => {
	if ('requestIdleCallback' in window) {
		requestIdleCallback(cb, { timeout: 5000 });
	} else {
		requestAnimationFrame(cb);
	}
};

export class ConfluenceSmartCardClient extends CardClient {
	private static readonly hydratedSSRStates: WeakSet<CardStore> = new WeakSet();
	onResolve: onResolveFn;
	private readonly editorCardProvider: ConfluenceEditorCardProvider;
	private readonly smartCardLocalCacheClient: SmartCardLocalCacheClient;

	constructor(props: { onResolve: onResolveFn; envKey?: EnvironmentsKeys }) {
		const { onResolve, envKey } = props;
		super(envKey);
		this.onResolve = onResolve;

		// Because Card components uses data from client and it's super hard to pass ConfluenceEditorCardProvider to them,
		// we will create a new ConfluenceEditorCardProvider instance here and use it for SSR support.
		this.editorCardProvider = new ConfluenceEditorCardProvider(
			this.envKey as EnvironmentsKeys,
			this.baseUrlOverride,
		);

		this.smartCardLocalCacheClient = SmartCardLocalCacheClient.getInstance();
		// Fire a client-side exposure for the SSR preload experiment. The experiment is only
		// evaluated in SSR preload code where the Statsig JS SDK is not initialized, so exposures
		// never reach Statsig from SSR alone. Calling manuallyLogExpExposure here — at the exact
		// point where the SSR-preloaded data is consumed on the client — ensures every page load
		// records an exposure, allowing us to measure the experiment's effect accurately.
		// Once the optimal value is found, this will be converted to a permanent Statsig config.
		manuallyLogExpExposure('cc_editor_node_data_providers_preload_config');
		const ssrProvidersData: {
			[key: string | 'editorCardProvider']: Record<string, JsonLd.Response>;
		} = window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] || {};
		const ssrData = ssrProvidersData[this.editorCardProvider.name];
		this.editorCardProvider.setSSRData(ssrData);
	}

	// Called once per page load
	prefetchData(url: string, appearance?: CardAppearance): ReturnType<CardClient['prefetchData']> {
		return super.prefetchData(url, appearance).then((data) => {
			// Using deferIdle(requestIdleCallback) to ensure our onResolve callback doesn't interrupt the smart card client.
			// This means there's a chance under heavy browser load that the delay between client (pre)fetch and onResolve invocation will be >1s, but usually it shouldn't be.
			// Given the data schema is unstable, we're setting the type as unknown and expecting individual resolvers to be mindful.
			data && deferIdle(() => this.onResolve(data as unknown));
			return data;
		});
	}

	// Called on load and on click of each smart card link
	async fetchData(
		url: string,
		force?: boolean,
		appearance?: CardAppearance,
	): ReturnType<CardClient['fetchData']> {
		// Keep inline resolves on the editor provider path only while the inline resolve
		// optimization is active. Disabling the experiment restores the previous
		// appearance-based parent fetch path without needing a code rollback.
		const shouldUseParentFetchData =
			appearance !== undefined &&
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			(appearance !== 'inline' || !fg('platform_smartlink_inline_resolve_optimization'));

		if (!shouldUseParentFetchData && (!force || this.smartCardLocalCacheClient.isUrlInCache(url))) {
			return this.editorCardProvider
				.getDataAsPromise_DO_NOT_USE_OUTSIDE_MIGRATIONS({
					// It's ok to cast any resourceUrl to inlineCard here, because only URL is important for the request.
					type: 'inlineCard',
					attrs: {
						url,
					},
				})
				.then((data) => {
					data && deferIdle(() => this.onResolve(data as unknown));
					return data;
				});
		}

		return super.fetchData(url, force, appearance).then((data) => {
			data && deferIdle(() => this.onResolve(data as unknown));
			return data;
		});
	}

	// Called after hydration, never during SSR. Seed each snapshot once so remounting
	// a provider cannot overwrite responses fetched after the initial page load.
	seedSSRSmartLinks(initialState: CardStore): void {
		if (
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			!fg('cc_preserve_smart_link_anchor_content') ||
			ConfluenceSmartCardClient.hydratedSSRStates.has(initialState)
		) {
			return;
		}
		ConfluenceSmartCardClient.hydratedSSRStates.add(initialState);

		for (const [url, { status, details }] of Object.entries(initialState)) {
			if (
				hasUrlFragment(url) &&
				status === 'resolved' &&
				details?.meta &&
				getStatus(details) === 'resolved'
			) {
				const cacheKey = this.editorCardProvider.nodeDataKey({
					type: 'inlineCard',
					attrs: { url },
				});
				// eslint-disable-next-line check-set-storage-usage/no-unimported-storage-set -- SmartCardLocalCacheClient writes through the platform StorageClient using its existing cache key.
				this.smartCardLocalCacheClient.setItem(cacheKey, details);
			}
		}
	}

	getInitialCardStoreFromCache = (): CardStore => {
		const initialStateCardStore: CardStore = {};
		const timestamps: Record<string, number> = {};
		const cachedData = this.smartCardLocalCacheClient.getCache();
		Object.entries(cachedData).forEach(([cacheKey, value]) => {
			const { metadataStatus, url } = getUrlAndMetadataStatus(cacheKey);
			const timestamp = value.timestamp ?? 0;
			const hasFragment = hasUrlFragment(url);
			// Prefer fresh anchor content over older full metadata. Inline SSR responses
			// remain pending so hovering can still fetch complete metadata.
			if (
				hasFragment &&
				timestamp < timestamps[url] &&
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				fg('cc_preserve_smart_link_anchor_content')
			) {
				return;
			}
			if (
				initialStateCardStore[url]?.metadataStatus === 'resolved' &&
				metadataStatus !== 'resolved' &&
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				(!fg('cc_preserve_smart_link_anchor_content') ||
					!hasFragment ||
					timestamp <= timestamps[url])
			) {
				return;
			}

			const details = value.data;
			// NAVX-4712: Handle responses with non-resolved statuses that were previously cached
			const status = details && details.meta ? getStatus(details) : undefined;
			if (status === 'resolved') {
				// When FG is on, preserve metadataStatus from the cache key (e.g. 'pending' for inline)
				// so hover cards can trigger a full block fetch. When FG is off, always use 'resolved'.
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				const resolvedMetadataStatus = fg('platform_smartlink_inline_resolve_optimization')
					? metadataStatus
					: 'resolved';
				initialStateCardStore[url] = { status, details, metadataStatus: resolvedMetadataStatus };
				timestamps[url] = timestamp;
			} else if (status !== undefined) {
				// Include forbidden/unauthorized responses so the card can render the correct error state
				initialStateCardStore[url] = { status, details, metadataStatus: 'resolved' };
				timestamps[url] = timestamp;
			}
		});
		return initialStateCardStore;
	};
}
